// Owned search component. Load after theme.js, which registers the accessible drawer base.
(() => {
  class PredictiveSearchDrawer extends customElements.get('drawer-content') {
    connectedCallback() {
      super.connectedCallback();
      if (this.hasAttribute('open')) this.loadImages();
      this.inputElement = this.querySelector('[name="q"]');
      this.drawerContentElement = this.querySelector('.drawer__content');
      this.drawerFooterElement = this.querySelector('.drawer__footer');
      this.loadingStateElement = this.querySelector('.predictive-search__loading-state');
      this.resultsElement = this.querySelector('.predictive-search__results');
      this.menuListElement = this.querySelector('.predictive-search__menu-list');
      this.delegate.on('input', '[name="q"]', () => {
        // Invalidate immediately, before the debounce, so an old response cannot flash.
        this.abortController?.abort();
        clearTimeout(this.searchTimer);
        this.resultsElement.replaceChildren();
        this.drawerFooterElement.hidden = true;
        const term = this.inputElement.value.trim();
        if (!term) this.showEmpty();
        else {
          if (this.menuListElement) this.menuListElement.hidden = true;
          this.searchTimer = setTimeout(() => this.search(term), 200);
        }
      });
      this.delegate.on('click', '[data-action="reset-search"]', () => {
        this.inputElement.value = '';
        this.inputElement.focus();
        this.inputElement.dispatchEvent(new Event('input', { bubbles: true }));
      });
    }
    attributeChangedCallback(name, oldValue, newValue) {
      if (name === 'open' && newValue !== null) this.loadImages();
      super.attributeChangedCallback(name, oldValue, newValue);
    }
    loadImages() {
      this.querySelectorAll('template[data-search-image]').forEach(template => {
        template.replaceWith(template.content);
      });
    }
    disconnectedCallback() {
      clearTimeout(this.searchTimer);
      this.abortController?.abort();
      super.disconnectedCallback();
    }
    showEmpty() {
      this.loadingStateElement.hidden = true;
      this.resultsElement.hidden = true;
      this.resultsElement.removeAttribute('aria-busy');
      this.drawerContentElement.classList.remove('drawer__content--center');
      if (this.menuListElement) this.menuListElement.hidden = false;
    }
    async search(term) {
      const controller = new AbortController();
      this.abortController = controller;
      this.loadingStateElement.hidden = false;
      this.resultsElement.hidden = true;
      this.resultsElement.setAttribute('aria-busy', 'true');
      this.drawerContentElement.classList.add('drawer__content--center');
      if (this.menuListElement) this.menuListElement.hidden = true;
      try {
        let predictive = false;
        try { predictive = JSON.parse(document.getElementById('shopify-features')?.textContent || '{}').predictiveSearch === true; } catch (_) {}
        const url = new URL(predictive ? window.themeVariables.routes.predictiveSearchUrl : window.themeVariables.routes.searchUrl, location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('section_id', predictive ? 'predictive-search' : 'predictive-search-compatibility');
        if (predictive) {
          url.searchParams.set('resources[limit]', '6');
          url.searchParams.set('resources[limit_scope]', 'each');
          url.searchParams.set('resources[type]', 'product,collection,query,article,page');
        }
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Search request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const section = html.getElementById(predictive ? 'shopify-section-predictive-search' : 'shopify-section-predictive-search-compatibility');
        if (!section?.innerHTML.trim()) throw new Error('Search response missing');
        if (controller.signal.aborted || this.inputElement.value.trim() !== term || !this.isConnected) return;
        this.resultsElement.replaceChildren(...section.childNodes);
        // Both the current list layout and the retained compatibility layout are supported.
        this.drawerFooterElement.hidden = !this.resultsElement.querySelector('.predictive-search__results-list, .predictive-search__results-categories');
      } catch (error) {
        if (controller.signal.aborted || this.inputElement.value.trim() !== term || !this.isConnected) return;
        this.resultsElement.replaceChildren(this.querySelector('[data-search-error]').content.cloneNode(true));
        this.drawerFooterElement.hidden = false;
      } finally {
        if (this.abortController === controller && !controller.signal.aborted && this.isConnected) {
          this.loadingStateElement.hidden = true;
          this.resultsElement.hidden = false;
          this.resultsElement.removeAttribute('aria-busy');
          this.drawerContentElement.classList.remove('drawer__content--center');
        }
      }
    }
  }
  if (!customElements.get('predictive-search-drawer')) customElements.define('predictive-search-drawer', PredictiveSearchDrawer);
})();
